<template>
  <div class="pinboard-container fill-height d-flex flex-column">
    <v-toolbar flat dense class="pinboard-header">
      <v-btn icon dark @click="closePinboard" class="mr-2 glass-btn-icon">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon color="primary" class="mr-3">mdi-pin</v-icon>
      <v-toolbar-title class="font-weight-bold white--text pl-0" style="font-size: 1.1rem"> Pinboard </v-toolbar-title>
      <v-spacer></v-spacer>

      <div class="d-flex align-center glass-toggle px-3 py-1 rounded-pill">
        <span class="caption white--text mr-2" :class="{ 'opacity-50': !showGlobalPins }">Global</span>
        <v-switch v-model="showGlobalPins" hide-details dense class="mt-0 pt-0 custom-switch" color="primary"></v-switch>
      </div>
    </v-toolbar>

    <v-progress-linear indeterminate v-if="isPinboardLoading" color="primary" background-opacity="0.1"></v-progress-linear>

    <div class="pinboard-content flex-grow-1 pa-4 custom-scrollbar">
      <div v-if="!isPinboardLoading && filteredPins.length === 0" class="empty-state d-flex flex-column align-center justify-center fill-height">
        <div class="glass-circle mb-4">
          <v-icon size="40" color="rgba(255,255,255,0.5)">mdi-pin-off-outline</v-icon>
        </div>
        <h3 class="white--text text-h6 font-weight-bold mb-1">Keine Pins</h3>
        <p class="grey--text text--lighten-1 caption text-center" style="max-width: 200px">
          Drücke lange auf eine Nachricht, um sie hier anzuheften.
        </p>
      </div>

      <v-scroll-y-transition group>
        <div v-for="pin in filteredPins" :key="pin.key" class="mb-3">
          <v-card class="glass-pin-card" @click="jumpToMessage(pin.originalPin)" rounded="xl" elevation="0">
            <div class="pin-header px-4 py-2 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon v-if="pin.isGlobal" x-small color="primary" class="mr-2">mdi-earth</v-icon>
                <v-icon v-else x-small color="amber lighten-3" class="mr-2">mdi-bookmark</v-icon>

                <span class="caption font-weight-bold" :class="pin.isGlobal ? 'primary--text' : 'amber--text text--lighten-3'">
                  {{ pin.isGlobal ? "Globaler Pin" : "Gemerkt" }}
                </span>
                <span class="mx-2 grey--text text--darken-2">•</span>
                <span class="caption grey--text text--lighten-1">{{ formatDate(pin.date) }}</span>
              </div>

              <v-chip x-small color="rgba(255,255,255,0.1)" class="white--text group-chip">
                {{ pin.group.name }}
              </v-chip>
            </div>

            <v-divider style="border-color: rgba(255, 255, 255, 0.05)"></v-divider>

            <div class="pa-4 d-flex">
              <v-avatar size="42" class="mr-3 elevation-4">
                <v-img v-if="pin.sender.bild" :src="'/img/profilbilder/' + pin.sender.bild"></v-img>
                <span v-else class="white--text font-weight-bold">{{ getInitials(pin.sender) }}</span>
              </v-avatar>

              <div class="flex-grow-1" style="min-width: 0">
                <div class="d-flex justify-space-between align-baseline mb-1">
                  <span class="white--text font-weight-bold text-subtitle-2"> {{ pin.sender.vorname }} {{ pin.sender.nachname }} </span>
                </div>

                <p class="grey--text text--lighten-2 body-2 mb-0 text-truncate-2" style="line-height: 1.4">
                  {{ pin.content }}
                </p>
              </div>

              <div class="d-flex align-self-center ml-2">
                <v-btn icon small class="glass-btn-jump">
                  <v-icon small color="white">mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-if="pin.isGlobal" class="px-4 pb-2 pt-0">
              <span class="caption grey--text text--darken-1" style="font-style: italic"> Gepinnt von {{ pin.pinnedBy.vorname }} </span>
            </div>
          </v-card>
        </div>
      </v-scroll-y-transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"

export default {
  name: "PinboardView",
  data() {
    return {
      showGlobalPins: true,
    }
  },
  computed: {
    ...mapState("chatStore", ["pinboardPersonal", "pinboardGlobal", "isPinboardLoading"]),

    filteredPins() {
      const combined = []

      // 1. Persönliche Pins
      this.pinboardPersonal.forEach((msg) => {
        combined.push({
          key: `p-${msg._id}`,
          isGlobal: false,
          date: new Date(msg.createdAt),
          sender: msg.senderId || { vorname: "Unbekannt" },
          content: msg.textContent || (msg.fileInfo ? `📎 Datei: ${msg.fileInfo.originalName}` : "📷 Medium"),
          group: msg.groupId || { name: "Unbekannt" },
          pinnedBy: null,
          originalPin: msg,
        })
      })

      if (this.showGlobalPins) {
        this.pinboardGlobal.forEach((pin) => {
          if (!pin.messageId) return // Sicherheitscheck

          const msg = pin.messageId // Das ist das eigentliche Nachrichten-Objekt

          combined.push({
            key: `g-${msg._id}`,
            isGlobal: true,
            date: new Date(pin.createdAt),
            sender: msg.senderId || { vorname: "Unbekannt" },
            // Fallback für Content, falls es eine Datei ist
            content: msg.textContent || (msg.fileInfo ? `📎 Datei: ${msg.fileInfo.originalName}` : "📷 Medium"),
            group: pin.groupId || { name: "Unbekannt" },
            pinnedBy: pin.pinnedBy || { vorname: "Admin" },

            // --- HIER WAR DER FEHLER ---
            // FALSCH: originalPin: pin
            // RICHTIG: Wir brauchen die Nachricht (msg), nicht den Pin-Wrapper
            originalPin: msg,
            // ---------------------------
          })
        })
      }

      return combined.sort((a, b) => b.date - a.date)
    },
  },
  methods: {
    ...mapActions("chatStore", ["setActiveGroup"]),
    closePinboard() {
      this.setActiveGroup(null)
    },
    jumpToMessage(originalPin) {
      // Das Event wird vom Parent (chatView) gefangen
      this.$emit("jump-to-message", originalPin)
    },
    getInitials(user) {
      if (!user) return "?"
      const v = user.vorname || ""
      const n = user.nachname || ""
      return `${v.charAt(0).toUpperCase()}${n.charAt(0).toUpperCase()}`
    },
    formatDate(date) {
      return date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })
    },
  },
}
</script>

<style lang="scss" scoped>
.pinboard-container {
  /* Gleicher geiler Hintergrund wie im Chat */
  background-color: #0f0f0f;
  background-image: radial-gradient(circle at 100% 0%, rgba(0, 150, 63, 0.05), transparent 40%),
    radial-gradient(circle at 0% 100%, rgba(0, 0, 0, 0.8), transparent 40%);
  position: relative;
}

.pinboard-header {
  background: rgba(20, 20, 20, 0.85) !important;
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 10;
}

.glass-btn-icon {
  background: rgba(255, 255, 255, 0.05);
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.glass-toggle {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.custom-switch {
  transform: scale(0.9);
}

.pinboard-content {
  overflow-y: auto;
  padding-bottom: 40px;
}

/* Scrollbar nice machen */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

/* Die Pins: Glassmorphism pur */
.glass-pin-card {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease-out;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: rgba(255, 255, 255, 0.06) !important;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.pin-header {
  background: rgba(0, 0, 0, 0.2);
}

.group-chip {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.glass-btn-jump {
  background: rgba(255, 255, 255, 0.05);
}

.glass-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.opacity-50 {
  opacity: 0.5;
}
</style>
